import { ApiInstance, call } from '@advanza/api'
import { SelectSimple } from '@advanza/input'
import { Button, Divider, Icon } from '@advanza/ui'
import clsx from 'clsx'
import { useCurrentCountry } from 'hooks/miscHooks'
import { ApiService, AssignmentData, PreviewData } from 'interfaces/ProviderPresenceSubscriptions'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

const ProviderPresenceSubscriptionsMisc = () => {
    const [serviceCategories, setServiceCategories] = useState<ApiService[]>([])
    const [selectedServiceId, setSelectedServiceId] = useState(0)
    const [loading, setLoading] = useState(false)
    const [previewData, setPreviewData] = useState<PreviewData | null>(null)
    const [assignData, setAssignData] = useState<AssignmentData | null>(null)
    const [exportScheduled, setExportScheduled] = useState(false)
    const [error, setError] = useState('')
    const currentCountry = useCurrentCountry()

    useEffect(() => {
        if (currentCountry) {
            loadServiceCategories()
        }
    }, [currentCountry])

    const loadServiceCategories = async () => {
        try {
            setLoading(true)
            setError('')

            const response = await call(
                `office/provider-presence-subscriptions/service-categories?countryCode=${currentCountry}`,
            )

            if (response && response.status === 'success') {
                setServiceCategories(response.data)
                if (response.data.length > 0) {
                    setSelectedServiceId(response.data[0].service_id)
                }
            } else {
                setError(response?.error || 'Failed to load service categories')
            }
        } catch (err) {
            setError('Error loading service categories: ' + (err as Error).message)
        } finally {
            setLoading(false)
        }
    }

    const handlePreview = async () => {
        if (!selectedServiceId) {
            setError('Please select a service category')
            return
        }

        try {
            setLoading(true)
            setError('')
            setPreviewData(null)
            setAssignData(null)

            const response = await call(
                `office/provider-presence-subscriptions/preview/${selectedServiceId}`,
            )

            if (response.status === 'success') {
                setPreviewData(response)
            } else {
                setError(response.error || 'Failed to load preview data')
            }
        } catch (err) {
            setError('Error loading preview: ' + (err as Error).message)
        } finally {
            setLoading(false)
        }
    }

    const handleExport = () => {
        if (!selectedServiceId) {
            setError('Please select a service category')
            return
        }
    }

    const handleScheduleExport = async () => {
        if (!selectedServiceId) {
            setError('Please select a service category')
            return
        }

        try {
            setLoading(true)
            setError('')
            setExportScheduled(false)

            const response = await call(
                `office/provider-presence-subscriptions/schedule-export/${selectedServiceId}`,
                {
                    method: 'POST',
                },
            )

            if (response.status === 'success') {
                if (response.scheduled) {
                    setExportScheduled(true)
                } else {
                    setError(
                        'Export could not be scheduled (possibly running locally). Please use the direct export option instead.',
                    )
                }
            } else {
                setError(response.error || 'Failed to schedule export')
            }
        } catch (err) {
            setError('Error scheduling export: ' + (err as Error).message)
        } finally {
            setLoading(false)
        }
    }

    const handleAssign = async () => {
        if (!selectedServiceId) {
            setError('Please select a service category')
            return
        }

        if (
            !confirm(
                'This will create presence subscriptions and apply discounts to all qualifying providers. Are you sure you want to continue?',
            )
        ) {
            return
        }

        try {
            setLoading(true)
            setError('')
            setAssignData(null)
            setPreviewData(null)

            const response = await call(
                `office/provider-presence-subscriptions/assign/${selectedServiceId}`,
                {
                    method: 'POST',
                },
            )

            if (response.status === 'success') {
                setAssignData(response)
            } else {
                setError(response.error || 'Failed to assign discounts')
            }
        } catch (err) {
            setError('Error assigning discounts: ' + (err as Error).message)
        } finally {
            setLoading(false)
        }
    }

    const getSelectedServiceName = () => {
        const service = serviceCategories.find((s) => s.service_id === selectedServiceId)
        return service ? service.name : ''
    }

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-end gap-3">
                <div>
                    <SelectSimple
                        legend
                        placeholder="Service category"
                        options={serviceCategories.map((service) => ({
                            name: service.name,
                            value: service.service_id,
                        }))}
                        value={selectedServiceId}
                        onChange={(e) => setSelectedServiceId(Number(e.target.value))}
                        disabled={loading}
                    />
                </div>

                <Button
                    onClick={handlePreview}
                    disabled={loading || !selectedServiceId}
                    before={<Icon name="eye" />}
                    appearance="secondary"
                    className="h-11">
                    Preview Data
                </Button>

                <Link
                    to={`${ApiInstance.getAddress()}office/provider-presence-subscriptions/export/${selectedServiceId}`}
                    target="_blank"
                    onClick={handleExport}>
                    <Button
                        disabled={loading || !selectedServiceId}
                        before={<Icon name="download" />}
                        appearance="secondary"
                        className="h-11">
                        Export Excel (Direct)
                    </Button>
                </Link>

                <Button
                    onClick={handleScheduleExport}
                    disabled={loading || !selectedServiceId || exportScheduled}
                    before={<Icon name={exportScheduled ? 'check' : 'schedule'} />}
                    appearance="primary"
                    className="h-11">
                    {exportScheduled ? 'Export Scheduled' : 'Schedule Export'}
                </Button>

                <Button
                    onClick={handleAssign}
                    disabled={loading || !selectedServiceId}
                    before={<Icon name="plus" />}
                    appearance="primary"
                    className="h-11">
                    Create Subscriptions
                </Button>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                    {error}
                </div>
            )}

            {exportScheduled && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
                    Export task has been scheduled! You will receive the file via Slack when ready.
                </div>
            )}

            {loading && (
                <div className="text-center py-4">
                    <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <p className="mt-2 text-neutral-600">Processing...</p>
                </div>
            )}

            {(previewData || assignData) && (
                <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4">
                    <h4 className="font-medium text-neutral-900 mb-3">
                        {previewData
                            ? `Preview for ${getSelectedServiceName()}`
                            : `Assignment Results for ${getSelectedServiceName()}`}
                    </h4>

                    {previewData && (
                        <>
                            <div className="grid grid-cols-3 gap-4 mb-4">
                                <div className="text-center">
                                    <div className="text-2xl font-bold text-blue-600">
                                        {previewData.total_count}
                                    </div>
                                    <div className="text-md text-neutral-600">Total Providers</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-2xl font-bold text-orange-400">
                                        {previewData.summary.with_subscriptions_count}
                                    </div>
                                    <div className="text-md text-neutral-600">
                                        With Subscriptions
                                    </div>
                                </div>
                                <div className="text-center">
                                    <div className="text-2xl font-bold text-red-600">
                                        {previewData.summary.without_subscriptions_count}
                                    </div>
                                    <div className="text-md text-neutral-600">
                                        Without Subscriptions
                                    </div>
                                </div>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="min-w-full text-md">
                                    <thead>
                                        <tr className="border-b border-neutral-200">
                                            <th className="text-left py-2">Provider ID</th>
                                            <th className="text-left py-2">Business Name</th>
                                            <th className="text-left py-2">Username</th>
                                            <th className="text-left py-2">Leads 2025</th>
                                            <th className="text-left py-2">Impressions</th>
                                            <th className="text-left py-2">60 Days Pause</th>
                                            <th className="text-left py-2">Subscription</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {previewData.data.map((provider) => (
                                            <tr
                                                key={provider.provider_id}
                                                className="border-b border-neutral-100">
                                                <td className="py-2">{provider.provider_id}</td>
                                                <td className="py-2">{provider.business_name}</td>
                                                <td className="py-2">{provider.email}</td>
                                                <td className="py-2">{provider.leads_in_2025}</td>
                                                <td className="py-2">
                                                    {provider.impressions_last_4_weeks}
                                                </td>
                                                <td className="py-2">
                                                    {provider.has_60_days_pause}
                                                </td>
                                                <td className="py-2">
                                                    <span
                                                        className={clsx(
                                                            'inline-flex px-2 py-1 text-sm font-semibold rounded-full',
                                                            provider.has_active_presence_subscription ===
                                                                'Yes'
                                                                ? 'bg-orange-100 text-orange-800'
                                                                : 'bg-neutral-100 text-neutral-800',
                                                        )}>
                                                        {provider.has_active_presence_subscription}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {previewData.total_count > 10 && (
                                <p className="text-md text-neutral-500 mt-3">
                                    Showing first 10 of {previewData.total_count} providers
                                </p>
                            )}
                        </>
                    )}

                    {assignData && (
                        <>
                            <div className="grid grid-cols-5 gap-4 mb-4">
                                <div className="text-center">
                                    <div className="text-2xl font-bold text-blue-600">
                                        {assignData.summary.total_processed}
                                    </div>
                                    <div className="text-md text-neutral-600">Total Processed</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-2xl font-bold text-green-500">
                                        {assignData.summary.subscriptions_created}
                                    </div>
                                    <div className="text-md text-neutral-600">
                                        Subscriptions Created
                                    </div>
                                </div>
                                <div className="text-center">
                                    <div className="text-2xl font-bold text-purple-600">
                                        {assignData.summary.discounts_applied}
                                    </div>
                                    <div className="text-md text-neutral-600">
                                        Discounts Applied
                                    </div>
                                </div>
                                <div className="text-center">
                                    <div className="text-2xl font-bold text-orange-400">
                                        {assignData.summary.skipped_existing_subscriptions}
                                    </div>
                                    <div className="text-md text-neutral-600">
                                        Skipped (Existing)
                                    </div>
                                </div>
                                <div className="text-center">
                                    <div className="text-2xl font-bold text-red-600">
                                        {assignData.summary.errors}
                                    </div>
                                    <div className="text-md text-neutral-600">Errors</div>
                                </div>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="min-w-full text-md">
                                    <thead>
                                        <tr className="border-b border-neutral-200">
                                            <th className="text-left py-2">Provider ID</th>
                                            <th className="text-left py-2">Business Name</th>
                                            <th className="text-left py-2">Subscription Created</th>
                                            <th className="text-left py-2">Discount Applied</th>
                                            <th className="text-left py-2">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {assignData.results.map((result) => (
                                            <tr
                                                key={result.provider_id}
                                                className="border-b border-neutral-100">
                                                <td className="py-2">{result.provider_id}</td>
                                                <td className="py-2">{result.business_name}</td>
                                                <td className="py-2">
                                                    <span
                                                        className={clsx(
                                                            'inline-flex px-2 py-1 text-sm font-semibold rounded-full',
                                                            result.subscription_created
                                                                ? 'bg-green-200 text-green-800'
                                                                : 'bg-neutral-100 text-neutral-800',
                                                        )}>
                                                        {result.subscription_created ? 'Yes' : 'No'}
                                                    </span>
                                                </td>
                                                <td className="py-2">
                                                    <span
                                                        className={clsx(
                                                            'inline-flex px-2 py-1 text-sm font-semibold rounded-full',
                                                            result.discount_applied > 0
                                                                ? 'bg-purple-100 text-purple-800'
                                                                : 'bg-neutral-100 text-neutral-800',
                                                        )}>
                                                        {result.discount_applied > 0
                                                            ? `${result.discount_applied} discount(s)`
                                                            : 'No'}
                                                    </span>
                                                </td>
                                                <td className="py-2">
                                                    <span
                                                        className={clsx(
                                                            'inline-flex px-2 py-1 text-sm font-semibold rounded-full',
                                                            result.error
                                                                ? 'bg-red-100 text-red-800'
                                                                : 'bg-green-200 text-green-800',
                                                        )}>
                                                        {result.error ? 'Error' : 'Success'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {assignData.summary.total_processed > 10 && (
                                <p className="text-md text-neutral-500 mt-3">
                                    Showing first 10 of {assignData.summary.total_processed}{' '}
                                    providers
                                </p>
                            )}
                        </>
                    )}
                </div>
            )}

            <Divider m />

            <div className="text-md text-neutral-600">
                <p>
                    <strong>Criteria:</strong> Providers with main service = selected category
                </p>
                <p>
                    <strong>Exclusions:</strong> Providers with specific pause statuses, or overdue
                    invoices
                </p>
                <p>
                    <strong>Discounts:</strong> 100% first month + 50% for 2 additional months
                </p>
            </div>
        </div>
    )
}

export default ProviderPresenceSubscriptionsMisc
